import { Redirect, Route, Switch } from "react-router-dom";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { lazyWithRetry } from "@monorepo/helpers";
import { useGetCartGroupsWithTrashQuery } from "@monorepo/services/api/carts";
import { IHistorySliceState } from "@monorepo/types";
import { reverse } from "named-urls";

import { useAppSelector } from "hooks";

const HistoryOrder = lazyWithRetry(() => import("@monorepo/components/src/History/HistoryOrder/HistoryOrder"));
const HistoryServiceGroup = lazyWithRetry(
  () => import("@monorepo/components/src/History/HistoryServiceGroup/HistoryServiceGroup")
);
const HistoryHome = lazyWithRetry(() => import("@monorepo/components/src/History/HistoryHome/HistoryHome"));
const HistoryDateFilter = lazyWithRetry(
  () => import("@monorepo/components/src/History/HistoryDateFilter/HistoryDateFilter")
);
const HistorySearch = lazyWithRetry(() => import("@monorepo/components/src/History/HistorySearch/HistorySearch"));
const HistoryMonthSummary = lazyWithRetry(
  () => import("@monorepo/components/src/History/HistoryMonthSummary/HistoryMonthSummary")
);
const HistoryMonthOverview = lazyWithRetry(
  () => import("@monorepo/components/src/History/HistoryMonthOverview/HistoryMonthOverview")
);

interface IProps {
  orderId?: string;
  serviceId?: string;
  cartGroupId?: string;
  period?: string;
}

export const History = ({ orderId, serviceId, cartGroupId, period }: IProps) => {
  const { data: cartGroups } = useGetCartGroupsWithTrashQuery();

  const activeCart = useAppSelector(({ historySlice }: IHistorySliceState) => historySlice?.activeCart);

  const renderHistoryHome = () => {
    if (cartGroupId) {
      return <HistoryHome cartGroupId={cartGroupId} />;
    }

    if (activeCart) {
      return (
        <Redirect
          to={reverse(routeConfig.dashboard.payments.history.cartGroup.period.self, {
            cartGroupId: activeCart.cartId,
            period: activeCart.period,
          })}
        />
      );
    }

    if (cartGroups?.[0]?.id) {
      return (
        <Redirect
          to={reverse(routeConfig.dashboard.payments.history.cartGroup.self, { cartGroupId: cartGroups[0].id })}
        />
      );
    }

    return null;
  };

  return (
    <Switch>
      <Route path={routeConfig.dashboard.payments.history.cartGroup.summary.self}>
        {period && cartGroupId ? <HistoryMonthSummary period={period} cartGroupId={cartGroupId} /> : null}
      </Route>
      <Route path={routeConfig.dashboard.payments.history.cartGroup.filter}>
        {cartGroupId && <HistoryDateFilter cartGroupId={cartGroupId} />}
      </Route>
      <Route path={routeConfig.dashboard.payments.history.cartGroup.search}>
        {cartGroupId && <HistorySearch cartGroupId={cartGroupId} />}
      </Route>
      <Route path={routeConfig.dashboard.payments.history.cartGroup.order.service}>
        {orderId && serviceId && cartGroupId ? (
          <HistoryServiceGroup
            serviceId={Number(serviceId)}
            cartGroupId={Number(cartGroupId)}
            orderId={Number(orderId)}
          />
        ) : null}
      </Route>
      <Route path={routeConfig.dashboard.payments.history.cartGroup.order.self}>
        {orderId && cartGroupId ? <HistoryOrder orderId={Number(orderId)} cartGroupId={Number(cartGroupId)} /> : null}
      </Route>
      <Route path={routeConfig.dashboard.payments.history.cartGroup.period.self}>
        {cartGroupId && period && <HistoryMonthOverview period={period} cartGroupId={cartGroupId} />}
      </Route>
      <Route
        path={[routeConfig.dashboard.payments.history.self, routeConfig.dashboard.payments.history.cartGroup.self]}
      >
        {renderHistoryHome()}
      </Route>
    </Switch>
  );
};
